import { _isNotBlank } from '@/littledash';
import type { WebhookEventType } from '@/model/WebhookConfiguration.model';
import { ApiService } from '@/support/ApiService';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form-latest';

export interface WebhookFormValues {
  name: string;
  url: string;
  headers: Array<{ key: string; value: string }>;
  events: Array<string>;
}

export const nameValidator = {
  required: 'Name is required',
  maxLength: { value: 255, message: 'Maximum of 255 characters' },
  validate: {
    notBlank: (value: string) => _isNotBlank(value) || 'Name is required',
  },
};

export interface EventSelectEvent {
  type: 'ADDED' | 'REMOVED';
  value: string;
}

/** RFC 7230 token pattern — matches the server-side Java validation exactly. */
export const HEADER_KEY_PATTERN = /^[!#$%&'*+\-.0-9A-Z^_`a-z|~]+$/;

export const headerKeyValidator = (value: string): true | string => {
  if (!_isNotBlank(value)) {
    return true;
  }
  if (!HEADER_KEY_PATTERN.test(value)) {
    return 'Header key contains invalid characters';
  }
  return true;
};

export const URL_MAX_LENGTH = 800;

export const urlValidator = (value: string) => {
  if (value.length > URL_MAX_LENGTH) {
    return `URL must be ${URL_MAX_LENGTH} characters or fewer`;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') {
      return 'URL must use HTTPS';
    }
    return true;
  } catch {
    return 'Please enter a valid URL';
  }
};

/**
 * Checks whether a webhook URL is unique for the current tenant via the API.
 * Returns true if unique, or an error message string if not.
 */
export const checkWebhookUrlUnique = async (url: string): Promise<true | string> => {
  try {
    const result = await ApiService.call({
      endpoint: 'POST /api/v1/webhooks/url-unique',
      body: { url },
      options: { onError: { toast: false, throw: true, capture: false, slug: 'webhook-url-unique' } },
    });
    return result?.body?.success === true ? true : 'This URL is already in use by another webhook';
  } catch {
    return 'Unable to verify URL uniqueness';
  }
};

const URL_UNIQUE_DEBOUNCE_MS = 350;

/**
 * Debounced version of checkWebhookUrlUnique. Each call cancels the previous pending
 * check and waits before firing. Returns a stable function reference per component via useRef.
 */
export const useDebouncedUrlUnique = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  return useCallback((url: string): Promise<true | string> => {
    clearTimeout(timerRef.current);
    return new Promise((resolve) => {
      timerRef.current = setTimeout(() => {
        resolve(checkWebhookUrlUnique(url));
      }, URL_UNIQUE_DEBOUNCE_MS);
    });
  }, []);
};

export const arraysEqual = (a: Array<string>, b: Array<string>): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
};

export const headerKeyRules = (index: number, getValues: UseFormGetValues<WebhookFormValues>) => ({
  validate: {
    format: headerKeyValidator,
    pairComplete: (value: string) => {
      const siblingValue = getValues(`headers.${index}.value`);
      if (!_isNotBlank(value) && _isNotBlank(siblingValue)) {
        return 'Header key is required when value is provided';
      }
      return true;
    },
  },
  maxLength: { value: 255 as const, message: 'Maximum of 255 characters' },
});

export const headerValueRules = (index: number) => ({
  maxLength: { value: 255 as const, message: 'Maximum of 255 characters' },
  deps: [`headers.${index}.key` as const],
});

/**
 * Wraps register so that header field changes also trigger array-level validation,
 * ensuring FieldArrayError messages clear when the user fills in empty rows.
 */
export const useRegisterHeader = (
  register: UseFormRegister<WebhookFormValues>,
  trigger: UseFormTrigger<WebhookFormValues>
): UseFormRegister<WebhookFormValues> =>
  useCallback(
    (...args: Parameters<typeof register>) => {
      const registration = register(...args);
      const originalOnChange = registration.onChange;
      return {
        ...registration,
        onChange: async (event: ChangeEvent<HTMLInputElement>) => {
          await originalOnChange(event);
          trigger('headers');
        },
      };
    },
    [register, trigger]
  ) as unknown as UseFormRegister<WebhookFormValues>;

/**
 * Shared hook for webhook event selection logic used by both AddWebhook and EditWebhook modals.
 */
export const useWebhookEvents = (
  eventTypes: Array<WebhookEventType>,
  events: Array<string> | undefined,
  setValue: UseFormSetValue<WebhookFormValues>
) => {
  const webhookEventSections = useMemo(
    () => [
      {
        items: eventTypes.map((eventType) => ({
          id: eventType.type,
          title: eventType.type,
        })),
      },
    ],
    [eventTypes]
  );

  const handleSetSelected = useCallback(
    (selected: Array<string>) => {
      setValue('events', selected, { shouldValidate: true });
    },
    [setValue]
  );

  const handleOnSelect = useCallback(
    ({ type, value }: EventSelectEvent) => {
      const current = events ?? [];
      if (type === 'ADDED' && !current.includes(value)) {
        setValue('events', [...current, value], { shouldValidate: true });
      } else if (type === 'REMOVED') {
        setValue(
          'events',
          current.filter((v) => v !== value),
          { shouldValidate: true }
        );
      }
    },
    [events, setValue]
  );

  const handleSelectAll = useCallback(() => {
    const allEvents = eventTypes.map((eventType) => eventType.type);
    setValue('events', allEvents, { shouldValidate: true });
  }, [eventTypes, setValue]);

  return { webhookEventSections, handleSetSelected, handleOnSelect, handleSelectAll };
};
